"use client";

import Button from "@/components/Button/Button.component";
import { BUTTON_SUBTYPE_CLASS } from "@/components/Button/Button.model";
import CapabilityServiceCard from "@/components/CapabilityServiceCard/CapabilityServiceCard.component";
import Card from "@/components/Card/Card.component";
import { Typography } from "@/components/Typography/Typography.component";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { cn, getTextColourBasedOnBackground } from "@/lib/utils";
// Import the polyfill for React 18+ compatibility
import "@/lib/findDOMNodePolyfill";
import { COLOR_PICKER } from "@/types/Contentful.model";
import { useCallback, useEffect, useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/keyboard";
import "swiper/css/mousewheel";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  FreeMode,
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { CapabilityServiceCardPropsModel } from "../CapabilityServiceCard/CapabilityServiceCard.model";
import {
  CardTypes,
  CustomSwiperPropsModel,
  MoreCapabilitiesFields,
  PeopleProfileFields,
  SliderCardFields,
} from "./CustomSwiper.model";
import "./CustomSwiper.style.css";

const CustomSwiper = ({
  __typename,
  groupTitle,
  title,
  subTitle,
  peopleProfileCollection,
  capabilitiesOrderCollection,
  capabilityOfferingsCollection,
  sliderCardCollection,
  cta,
  backgroundColor = { name: "white", value: "#ffffff" },
}: CustomSwiperPropsModel) => {
  const data =
    peopleProfileCollection ||
    capabilitiesOrderCollection ||
    capabilityOfferingsCollection ||
    sliderCardCollection;
  const { isMobile, isDesktop, isTablet } = useBreakpoints();
  const nextButtonClass = `swiper-button-next-${__typename}`;
  const prevButtonClass = `swiper-button-prev-${__typename}`;
  const cssClass = getTextColourBasedOnBackground(backgroundColor?.name);

  const getCardContent = (card: CardTypes) => {
    if (!card) return;

    switch (card.__typename) {
      case "ComponentCapabilityServices":
        return (
          <CapabilityServiceCard
            title={(card as CapabilityServiceCardPropsModel).title}
            description={(card as CapabilityServiceCardPropsModel).description}
          />
        );
      case "ComponentSliderCard":
        return (
          <Card
            className={cn(
              "rounded-btn hover:text-core-white h-80 border p-4 hover:border",
              `hover:bg-${COLOR_PICKER[(card as SliderCardFields).backgroundColor.name as keyof typeof COLOR_PICKER]}`,
            )}
            title={(card as SliderCardFields).name}
            overlayText={(card as SliderCardFields).quote}
            thumbnail={(card as PeopleProfileFields).featureImage}
            slug={""}
          />
        );
      default:
        return (
          <Card
            brand={(card as PeopleProfileFields).name}
            name={(card as PeopleProfileFields).designation}
            thumbnail={(card as PeopleProfileFields).featureImage}
            slug={
              (card as MoreCapabilitiesFields)?.slug
                ? `/capability/${(card as MoreCapabilitiesFields).slug}`
                : ""
            }
            overlayText={(card as PeopleProfileFields).quote}
          />
        );
    }
  };

  const getSlidesPerView = useCallback((): number | "auto" => {
    if (data?.some((card: any) => card.__typename === "CapabilityOffering")) {
      if (isMobile) {
        return 1.2881;
      } else if (isDesktop) {
        return window.innerWidth > 1711 ? 4.8 : 4;
      } else if (isTablet) {
        return 2.2;
      } else {
        return 3.8;
      }
    } else {
      if (isMobile) {
        return 1.32;
      } else if (isDesktop) {
        return 3.8;
      } else {
        return "auto";
      }
    }
  }, [data, isMobile, isDesktop, isTablet]);

  const [slidesPerView, setSlidesPerView] = useState(getSlidesPerView());

  useEffect(() => {
    const handleResize = () => {
      setSlidesPerView(getSlidesPerView());
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [getSlidesPerView]);

  return (
    <div
      className={cn(
        backgroundColor?.name?.toLowerCase() === "black" ||
          backgroundColor?.name?.toLowerCase() === "white"
          ? `bg-core-${backgroundColor?.name?.toLowerCase()} ${cssClass}`
          : `bg-core-${backgroundColor?.name?.toLowerCase()} ${cssClass}`,
        "h-full w-screen px-4 py-20 pb-40 md:px-8 lg:px-10",
      )}
    >
      <div className="flex justify-between">
        {groupTitle ? (
          <Typography.LABEL1>{groupTitle}</Typography.LABEL1>
        ) : (
          <Typography.LLABEL2 className="pb-2">{title}</Typography.LLABEL2>
        )}
        {cta && (
          <Button
            primary={false}
            type={
              cssClass === "text-core-black"
                ? BUTTON_SUBTYPE_CLASS.SECONDARY_BLACK
                : BUTTON_SUBTYPE_CLASS.SECONDARY_WHITE
            }
            content={{ ...cta }}
            className={cn(`!${cssClass}`, "w-auto")}
          />
        )}
      </div>
      <Typography.BODY2>{subTitle}</Typography.BODY2>

      <Swiper
        spaceBetween={40}
        modules={[Navigation, Pagination, FreeMode, Keyboard, Mousewheel]}
        pagination={{
          type: "progressbar",
        }}
        className="mySwiper !py-10"
        navigation={{
          nextEl: `.${nextButtonClass}`,
          prevEl: `.${prevButtonClass}`,
        }}
        keyboard={{ enabled: true }}
        allowTouchMove={true}
        freeMode={true}
        mousewheel={isMobile ? false : { forceToAxis: true }}
        nested={true}
        slidesPerView={slidesPerView}
        preventClicks={true}
        preventClicksPropagation={true}
        touchMoveStopPropagation={true}
      >
        {data?.map((card: any, index: number) => (
          <SwiperSlide key={index} className="!mr-8">
            <div className={cn(`${cssClass}`, "relative w-full")}>
              {getCardContent(card)}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="absolute right-4 mt-12 flex gap-x-4 md:right-8 lg:right-10">
        <Typography.LLABEL2
          className={cn(prevButtonClass, "!w-auto cursor-pointer")}
        >
          {"[ < ]"}
        </Typography.LLABEL2>
        <Typography.LLABEL2
          className={cn(nextButtonClass, "!w-auto cursor-pointer")}
        >
          {"[ > ]"}
        </Typography.LLABEL2>
      </div>
    </div>
  );
};

export default CustomSwiper;
